/**
 * src/lib/voice/processUvtpScope.js
 *
 * Whats-next #1 — drop-in alternative to processProjectScope/processInAppScope
 * inside voiceProcessFn. Routes recording to UVTP (`/api/voice-transcribe`)
 * instead of the legacy `captureVoice` / `uploadVoiceFile` paths.
 *
 * Activation: gated by `VITE_UVTP_FOR_PROJECTS=1` env var. Off by default.
 * When the flag is set, voiceProcessFn routes project-scope recordings here
 * instead of `processProjectScope`. Other scope_types stay on the existing
 * paths until we extend.
 *
 * Flow:
 *   1. GET /api/voice-transcribe?action=upload-url → { uploadUrl, audioStorageKey }
 *   2. PUT the audio blob to uploadUrl (signed URL — no auth header needed)
 *   3. POST /api/voice-transcribe with { audioStorageKey, source, durationSec, language }
 *   4. Fast-path 200: updateRecording status='ready' + transcript_raw + uvtp_run_id
 *      Heavy-path 202: updateRecording status='processing' + uvtp_run_id, then
 *      kick pollUvtpRun (Plan 93-01) without awaiting — the outer worker
 *      promise resolves immediately so the queue can drain other recordings
 *      while the poll loop runs in the background.
 *
 * Errors propagate so the queue worker applies its standard retry+backoff.
 */

import { apiFetch } from '../api/client';
import { updateRecording } from '../voiceQueue';
import { pollUvtpRun, UvtpPollTimeoutError } from './uvtpPoller.js';
import { logEvent } from '../observability.js';
import { insertVoiceRecord, classifyOnly } from '../api/voice-v2.js';

const ENDPOINT = '/api/voice-transcribe';

/**
 * Resolve the trace_id for this recording. Phase 92 spine requires every
 * step to share the same trace_id; if the IDB row predates the spine and
 * has no trace_id, generate one and persist it back so the poller's
 * background logEvent rows correlate with whatever upstream trace exists.
 */
function ensureTraceId(rec) {
  if (rec?.trace_id) return rec.trace_id;
  const generated = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  // Persist back so the next read sees it; fire-and-forget. Wrap in
  // Promise.resolve so test mocks that return void don't blow up on .catch.
  try { Promise.resolve(updateRecording(rec.recording_id, { trace_id: generated })).catch(() => {}); }
  catch { /* fire-and-forget — never throw from trace_id default */ }
  return generated;
}

export async function processUvtpScope(rec) {
  if (!rec?.audio_blob) {
    throw new Error('[processUvtpScope] missing audio_blob on recording ' + (rec?.recording_id || '?'));
  }

  const traceId = ensureTraceId(rec);

  // 1. Get signed upload URL
  const upload = await apiFetch(`${ENDPOINT}?action=upload-url`);
  if (!upload?.uploadUrl || !upload?.audioStorageKey) {
    throw new Error('[processUvtpScope] upload-url response missing uploadUrl or audioStorageKey');
  }

  // 2. PUT audio to signed URL — note: signed URL embeds the auth token, so
  // we use raw fetch (apiFetch would attach our service-role JWT, which the
  // signed URL endpoint rejects with a confusing 400).
  const blob = rec.audio_blob;
  const putResp = await fetch(upload.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': blob?.type || 'audio/webm' },
    body: blob,
  });
  if (!putResp.ok) {
    const body = await putResp.text().catch(() => '');
    throw new Error(`[processUvtpScope] upload failed ${putResp.status}: ${body.slice(0, 200)}`);
  }

  // 3. POST audioStorageKey for transcription
  const postBody = {
    audioStorageKey: upload.audioStorageKey,
    source: 'assistant',
    durationSec: typeof rec.duration_sec === 'number' ? rec.duration_sec : undefined,
    language: rec.language || undefined,
    mimeType: blob?.type || 'audio/webm',
  };
  const result = await apiFetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(postBody),
  });

  // 4. Update recording based on path
  if (result?.status === 'complete') {
    // Plan 93-02 / TICKET-UVTP-PROJECT-PARITY: write voice_records +
    // voice_transcripts via insertVoiceRecord (atomic, inherits CARL
    // development-002 + 003 contracts), then mark IDB ready, then
    // fire-and-forget classifyOnly. Mirrors the legacy upload+drain
    // contract so the breadcrumb wall stays byte-equivalent.
    const voiceRecordId = await insertVoiceRecordForProject(
      rec, result.text || '', result.language, traceId,
    );
    if (voiceRecordId === null) return; // insert failed — IDB already marked 'error'

    await updateRecording(rec.recording_id, {
      status: 'ready',
      transcript_raw: result.text || null,
      uvtp_run_id: result.uvtpRunId || null,
      audio_blob: null, // free IDB space — transcript is now in voice_records
      voice_record_id: voiceRecordId,
    });

    fireAndForgetClassifier(voiceRecordId, traceId);
  } else if (result?.status === 'queued') {
    // Heavy path — transcript will arrive later via the poll loop.
    // We DON'T clear audio_blob yet in case a follow-up retry needs it.
    await updateRecording(rec.recording_id, {
      status: 'processing',
      uvtp_run_id: result.uvtpRunId || null,
    });

    // Kick the poller in the background (Plan 93-01 / TICKET-UVTP-POLLER).
    // Don't await — the worker should drain the next recording immediately.
    // The poller resolves the IDB transition to 'ready' on completion, OR
    // marks status='error' on poll-error / timeout.
    pollUvtpRun({ uvtpRunId: result.uvtpRunId, traceId })
      .then(async (poll) => {
        const transcriptText = poll.fullTranscript || poll.text || '';
        // Plan 93-02 — same insertVoiceRecord contract as the fast path
        const voiceRecordId = await insertVoiceRecordForProject(
          rec, transcriptText, rec.language, traceId,
        );
        if (voiceRecordId === null) return;

        await updateRecording(rec.recording_id, {
          status: 'ready',
          transcript_raw: transcriptText || null,
          audio_blob: null,
          voice_record_id: voiceRecordId,
        });

        fireAndForgetClassifier(voiceRecordId, traceId);
      })
      .catch(async (err) => {
        const errKind = err instanceof UvtpPollTimeoutError ? 'poll_timeout' : 'poll_error';
        try {
          await updateRecording(rec.recording_id, {
            status: 'error',
            error_message: `[uvtp:${errKind}] ${err?.message || String(err)}`.slice(0, 500),
          });
        } catch { /* IDB may be closed if the user navigated away */ }
        try {
          logEvent('uvtp_scope', 'heavy_path_failed', {
            trace_id: traceId,
            source: 'client',
            module: 'uvtp_scope',
            step: 'heavy_path_resolve',
            status: 'error',
            error_code: errKind,
            error_message: String(err?.message || err).slice(0, 200),
            payload: { recording_id: rec.recording_id, uvtpRunId: result.uvtpRunId },
          });
        } catch { /* logger never throws */ }
      });
  } else {
    throw new Error(
      `[processUvtpScope] unexpected response status: ${result?.status || 'undefined'}`
    );
  }
}

/**
 * Plan 93-02: shared helper called from both fast-path and heavy-path branches.
 * Returns voice_record_id on success, or null on failure (IDB already marked 'error').
 * Never throws — schema mismatches won't fix themselves on retry, so we mark the
 * row 'error' and let the user re-record rather than thrashing the queue.
 */
async function insertVoiceRecordForProject(rec, transcript, resolvedLanguage, traceId) {
  try {
    const inserted = await insertVoiceRecord({
      transcript,
      contextType: 'project',
      contextId: rec.scope_id,
      idempotencyKey: rec.recording_id,
      language: rec.language || resolvedLanguage || 'en',
      durationSeconds: typeof rec.duration_sec === 'number' ? rec.duration_sec : undefined,
    });
    return inserted?.voice_record_id || null;
  } catch (err) {
    try {
      logEvent('uvtp_scope', 'insert_voice_record_failed', {
        trace_id: traceId,
        source: 'client',
        module: 'uvtp_scope',
        step: 'insert_voice_record',
        status: 'error',
        error_message: String(err?.message || err).slice(0, 500),
        payload: { recording_id: rec.recording_id, scope_id: rec.scope_id },
      });
    } catch { /* spine-emit-ok: logger wrapper itself — outer catch already emitted error event before this try, no second emit needed */ }
    try {
      await updateRecording(rec.recording_id, {
        status: 'error',
        error_message: `[uvtp:insert] ${err?.message || String(err)}`.slice(0, 500),
      });
    } catch { /* spine-emit-ok: IDB write best-effort during error cleanup; outer catch already emitted insert_voice_record_failed event */ }
    return null;
  }
}

/**
 * Plan 93-02: fire-and-forget classifier kick. The classifier runs server-side
 * (~6-17s) and UPDATEs voice_records via realtime; we never await it because
 * the breadcrumb is already visible to the user. Mirrors processInAppScope.
 *
 * Plan 93-03: ALSO fires the brain-summary endpoint (also fire-and-forget).
 * Brain summary is non-blocking — if it fails or the tab closes mid-POST,
 * the breadcrumb is already visible. Future backfill cron can sweep
 * voice_records WHERE status='ready' AND brain_summary_at IS NULL.
 */
function fireAndForgetClassifier(voiceRecordId, traceId) {
  try {
    Promise.resolve(classifyOnly(voiceRecordId)).catch((err) => {
      try {
        logEvent('uvtp_scope', 'classifier_kick_failed', {
          trace_id: traceId,
          source: 'client',
          module: 'classifier_kick',
          step: 'kick',
          status: 'error',
          error_message: String(err?.message || err).slice(0, 200),
          payload: { voice_record_id: voiceRecordId },
        });
      } catch { /* spine-emit-ok: logger wrapper — outer .catch already emitted classifier_kick_failed event */ }
    });
  } catch { /* spine-emit-ok: fire-and-forget kick wrapper — sync throw before promise chain only happens if classifyOnly is undefined (unmocked test env), benign */ }

  // Plan 93-03 — brain-summary kick (non-blocking, no await)
  try {
    Promise.resolve(
      apiFetch('/api/data?action=brain.summarize-voice-record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_record_id: voiceRecordId, trace_id: traceId }),
      }),
    ).catch((err) => {
      try {
        logEvent('brain_summary', 'kick_failed', {
          trace_id: traceId,
          source: 'client',
          module: 'brain_summary',
          step: 'kick',
          status: 'error',
          error_message: String(err?.message || err).slice(0, 200),
          payload: { voice_record_id: voiceRecordId },
        });
      } catch { /* spine-emit-ok: logger wrapper — outer .catch already emitted brain_summary kick_failed event */ }
    });
  } catch { /* spine-emit-ok: fire-and-forget POST wrapper — sync throw before promise chain (apiFetch undefined in unmocked tests); benign */ }
}
